import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, Send, CalendarOff, Lock } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { api, money, prettyDate, prettyTs } from "./api";

const REQ_STYLES = {
  pending: "border-yellow-500/40 bg-yellow-400/10 text-yellow-700",
  approved: "border-emerald-500/40 bg-emerald-400/10 text-emerald-700",
  rejected: "border-red-500/40 bg-red-400/10 text-red-600",
};

const RATE_LABELS = [["min_rate", "Approved minimum"], ["standard_rate", "Standard"], ["peak_rate", "Peak"], ["offseason_rate", "Off-season"]];

const rangesText = (ranges) =>
  ranges?.length ? ranges.map((r) => `${prettyDate(r.start)} → ${prettyDate(r.end)}`).join(" · ") : "—";

export default function PortalPricing() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ location_id: "", proposed_min_rate: "", proposed_standard_rate: "", proposed_peak_rate: "", proposed_offseason_rate: "", effective_from: "", effective_to: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/portal/pricing").then(({ data }) => setData(data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load pricing"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/portal/pricing/requests", {
        ...form,
        proposed_min_rate: parseFloat(form.proposed_min_rate),
        proposed_standard_rate: parseFloat(form.proposed_standard_rate),
        proposed_peak_rate: parseFloat(form.proposed_peak_rate),
        proposed_offseason_rate: parseFloat(form.proposed_offseason_rate),
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
      });
      toast.success("Price-change request submitted for XE review");
      setForm({ location_id: form.location_id, proposed_min_rate: "", proposed_standard_rate: "", proposed_peak_rate: "", proposed_offseason_rate: "", effective_from: "", effective_to: "", reason: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <PortalShell title="Pricing"><p className="py-20 text-center text-sm text-muted">Loading…</p></PortalShell>;

  const activePlan = (locId) => data.plans.find((p) => p.location_id === locId && p.status === "active");
  const formValid = form.location_id && form.proposed_min_rate && form.proposed_standard_rate && form.proposed_peak_rate && form.proposed_offseason_rate && form.reason.trim().length >= 3;

  return (
    <PortalShell title="Pricing">
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-aqua/40 bg-aqua/[0.07] p-5" data-testid="pricing-explainer">
        <Lock size={18} className="mt-0.5 shrink-0 text-aqua" />
        <p className="text-sm leading-relaxed text-offwhite">
          <strong>XE has final authority over all Jet Car rental prices.</strong> You can view your approved rates,
          choose from approved rate plans, and submit price-change requests — requests become active only after XE
          administrator approval. Rentals always charge the XE-approved rate for the location, vehicle, duration and
          date. Your own add-ons (photography, celebrations, etc.) are recorded separately and excluded from Net Rental
          Revenue unless XE approves otherwise.
        </p>
      </div>

      <h2 className="mb-4 font-display text-lg font-bold text-offwhite">Approved Rates by Location</h2>
      <div className="grid gap-4 lg:grid-cols-2" data-testid="pricing-plans">
        {data.locations.map((loc) => {
          const plan = activePlan(loc.location_id);
          return (
            <div key={loc.location_id} className="card-line rounded-xl p-6" data-testid={`pricing-loc-${loc.location_id}`}>
              <p className="font-display text-base font-bold text-offwhite">{loc.name}</p>
              <p className="text-xs text-muted">{loc.address}</p>
              {!plan ? (
                <p className="mt-4 rounded-lg border border-orange-500/40 bg-orange-400/10 p-3 text-xs text-orange-700" data-testid="pricing-none">
                  No approved pricing plan yet — reservations are blocked until XE publishes one.
                </p>
              ) : (
                <>
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {RATE_LABELS.map(([k, label]) => (
                      <div key={k}>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">{label}</p>
                        <p className="mt-1 font-display text-xl font-extrabold text-offwhite" data-testid={`rate-${k}`}>{money(plan[k])}<span className="text-xs font-semibold text-muted">/hr</span></p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-1.5 text-xs text-muted">
                    <p>Minimum rental: <strong className="text-offwhite">{plan.min_duration_hours} hours</strong></p>
                    <p>Peak season: {rangesText(plan.peak_ranges)}</p>
                    <p>Off-season: {rangesText(plan.offseason_ranges)}</p>
                    <p className="flex items-center gap-1.5"><CalendarOff size={12} /> Blackout dates: {plan.blackout_dates?.length ? plan.blackout_dates.map(prettyDate).join(", ") : "none"}</p>
                    <p>Effective: {plan.effective_from ? prettyDate(plan.effective_from) : "immediately"} → {plan.effective_to ? prettyDate(plan.effective_to) : "open-ended"}</p>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 grid items-start gap-6 lg:grid-cols-2">
        <div className="card-line rounded-xl p-6">
          <h2 className="mb-1 flex items-center gap-2 font-display text-base font-bold text-offwhite"><Send size={15} className="text-aqua" /> Request a Price Change</h2>
          <p className="mb-4 text-[11px] text-muted/70">Requests are reviewed by an XE administrator. Approved requests publish automatically with a full audit trail.</p>
          <div className="space-y-3">
            <div>
              <label className="field-label" htmlFor="pcr-loc">Location</label>
              <select id="pcr-loc" className="field" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} data-testid="pcr-location">
                <option value="">Select location…</option>
                {data.locations.map((l) => <option key={l.location_id} value={l.location_id} className="bg-white">{l.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["proposed_min_rate", "Proposed minimum $/hr"], ["proposed_standard_rate", "Proposed standard $/hr"], ["proposed_peak_rate", "Proposed peak $/hr"], ["proposed_offseason_rate", "Proposed off-season $/hr"]].map(([k, label]) => (
                <div key={k}>
                  <label className="field-label" htmlFor={`pcr-${k}`}>{label}</label>
                  <input id={`pcr-${k}`} type="number" min="1" className="field" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`pcr-${k}`} />
                </div>
              ))}
              <div>
                <label className="field-label" htmlFor="pcr-from">Effective from (optional)</label>
                <input id="pcr-from" type="date" className="field" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="pcr-from" />
              </div>
              <div>
                <label className="field-label" htmlFor="pcr-to">Effective to (optional)</label>
                <input id="pcr-to" type="date" className="field" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} data-testid="pcr-to" />
              </div>
            </div>
            <div>
              <label className="field-label" htmlFor="pcr-reason">Reason</label>
              <textarea id="pcr-reason" rows={3} className="field resize-none" placeholder="e.g. Peak summer demand at our marina supports a higher peak rate…" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="pcr-reason" />
            </div>
            <button onClick={submit} disabled={busy || !formValid} className="btn-primary !py-2.5 !text-xs disabled:opacity-40" data-testid="pcr-submit">
              {busy ? "Submitting…" : "Submit for XE Approval"}
            </button>
          </div>
        </div>

        <div>
          <h2 className="mb-4 font-display text-lg font-bold text-offwhite">My Requests</h2>
          {data.requests.length === 0 ? (
            <p className="card-line rounded-xl py-10 text-center text-sm text-muted" data-testid="requests-empty">No requests yet.</p>
          ) : (
            <div className="space-y-3" data-testid="requests-list">
              {data.requests.map((r) => (
                <div key={r.request_id} className="card-line rounded-xl p-5" data-testid={`request-${r.request_id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs text-aqua">{r.request_id}</span>
                    <span className="text-sm font-semibold text-offwhite">{r.location_name}</span>
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${REQ_STYLES[r.status]}`}>{r.status}</span>
                    <span className="ml-auto text-[11px] text-muted">{prettyTs(r.created_at)}</span>
                  </div>
                  <p className="mt-2 text-xs text-muted">
                    {r.previous_rates ? `${money(r.previous_rates.standard_rate)} → ` : ""}<strong className="text-offwhite">{money(r.proposed_rates.standard_rate)}/hr standard</strong>
                    {" · "}min {money(r.proposed_rates.min_rate)} · peak {money(r.proposed_rates.peak_rate)} · off-season {money(r.proposed_rates.offseason_rate)}
                  </p>
                  <p className="mt-1 text-xs italic text-muted/80">"{r.reason}"</p>
                  {r.reviewed_by && <p className="mt-1 text-[11px] text-muted/60">Reviewed by {r.reviewed_by} · {prettyTs(r.reviewed_at)}{r.review_note ? ` — ${r.review_note}` : ""}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalShell>
  );
}
